/**
 * Graceful Creations — renders the saved items list.
 *
 * Depends on window.gcWishlist from gc-wishlist.js. That file is loaded in the
 * head and this one in the body; module scripts execute in document order, so
 * it is always defined by the time this runs.
 */

const root = document.querySelector('[data-gc-saved]');

if (root) {
  const statusEl = root.querySelector('[data-gc-saved-status]');
  const emptyEl = root.querySelector('[data-gc-saved-empty]');
  const gridEl = root.querySelector('[data-gc-saved-grid]');

  const currency = root.dataset.currency || 'AUD';
  const locale = root.dataset.locale || 'en-AU';

  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });

  const setStatus = (text) => {
    statusEl.textContent = text || '';
    statusEl.toggleAttribute('hidden', !text);
  };

  /** Shopify returns prices in cents. */
  const price = (cents) => money.format((cents || 0) / 100);

  async function fetchProduct(handle) {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
      // A deleted or unpublished product 404s. Treat it as gone rather than
      // an error worth showing anyone.
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  function card(product) {
    const li = document.createElement('li');
    li.className = 'gc-saved__card';

    const image = product.featured_image
      ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=520`
      : null;

    li.innerHTML = `
      <a href="${product.url}" tabindex="-1" aria-hidden="true">
        ${
          image
            ? `<img class="gc-saved__img" src="${image}" alt="" loading="lazy" width="260" height="260">`
            : '<span class="gc-saved__img"></span>'
        }
      </a>
      <div class="gc-saved__body">
        <a class="gc-saved__title" href="${product.url}">${product.title}</a>
        <span class="gc-saved__price">${price(product.price)}</span>
        ${product.available ? '' : '<span class="gc-saved__sold">Sold out</span>'}
        <button type="button" class="gc-saved__remove" data-remove="${product.handle}">Remove</button>
      </div>
    `;
    return li;
  }

  async function render() {
    const handles = window.gcWishlist.read();

    if (!handles.length) {
      setStatus('');
      gridEl.hidden = true;
      gridEl.replaceChildren();
      emptyEl.hidden = false;
      return;
    }

    setStatus('Loading your saved items…');
    emptyEl.hidden = true;

    const products = await Promise.all(handles.map(fetchProduct));

    // Drop anything that no longer resolves, so a deleted product does not sit
    // in storage forever quietly failing.
    const missing = handles.filter((h, i) => !products[i]);
    if (missing.length) {
      missing.forEach((h) => window.gcWishlist.toggle(h));
      window.gcWishlist.refresh();
    }

    const found = products.filter(Boolean);

    if (!found.length) {
      setStatus('');
      gridEl.hidden = true;
      emptyEl.hidden = false;
      return;
    }

    gridEl.replaceChildren(...found.map(card));
    gridEl.hidden = false;
    setStatus(
      found.length === 1 ? '1 saved item' : `${found.length} saved items`
    );
  }

  gridEl.addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove]');
    if (!button) return;
    window.gcWishlist.toggle(button.getAttribute('data-remove'));
    window.gcWishlist.refresh();
    render();
  });

  // Keep in step with hearts toggled elsewhere, including another tab.
  document.addEventListener(window.gcWishlist.EVENT, render);

  render();
}
